/**
 * vehicleRepaid — what the vehicle has been paid towards, and what is left of its price.
 *
 * THE OWNER'S RULING [D-B58]: the deposit counts towards the vehicle being paid off. The money
 * engine treats a vehicle as paid for when the deposit, less any refund, plus the vehicle share of
 * the weekly payments reach `sold_at`; from that point on the contract instalment stops paying for
 * the vehicle and starts recovering the advances that carry no weekly of their own. The Vehicle tab
 * has to count it the same way or it would say a vehicle is still owed for on the very day the
 * engine stops charging for it — one net deposit short, for ever, on every contract that reaches
 * the end. Contracts 62 and 89 are the live cases.
 *
 * IT IS ALSO RIGHT ON ITS OWN, TODAY, BEFORE ANY ENGINE CHANGE. The Overview tab already counts the
 * deposit as paid (`ContractInsightOverview.tsx`, `deposit.amount` into Repaid), and this tab did
 * not, so the two tabs of one contract have been a deposit apart the whole time. This closes that.
 *
 * PURE, AND FREE OF THE COMPONENT GRAPH, so vitest can hold it under node — the tab itself is not
 * rendered by any test in this repo. The tab keeps its own `modeMultiplier` and hands it in.
 */

/** Which of the tab's three views is showing. Only a contract with a management rate has the other two. */
export type VehicleShareMode = 'all' | 'partner' | 'management';

export interface VehicleRepaidInput {
    /** `contracts.sold_at` — the vehicle's selling price. Nought where none is recorded. */
    price: number;
    /** `allocation_summary.contract` — what the weekly payments have put on the vehicle. */
    contractAllocation: number;
    /**
     * `deposit.amount` — ALREADY NET of refunds, and null on a contract with no deposit.
     *
     * The payload carries the gross under `paid_total` and the refunds under `refunded_total`; this
     * key is the difference, which is what has actually been paid. A refund recorded later lowers it,
     * and the engine's own threshold rises by exactly the same amount on the same day.
     */
    netDeposit: number | null;
    /** Which view the tab is showing. */
    mode: VehicleShareMode;
    /** The mode's share of the vehicle, as the tab computes it from `management_rate`. 1 in the All view. */
    modeMultiplier: number;
}

export interface VehicleRepaid {
    /** Total Repayable — the selling price at the mode's share. */
    price: number;
    /** Repaid — the deposit and the vehicle allocations, at the mode's share. NOT clamped; see below. */
    repaid: number;
    /** Outstanding — what is left of the price, floored at nothing. */
    outstanding: number;
}

/**
 * The three cards' one derivation.
 *
 * ── THE DEPOSIT JOINS THE ALL VIEW AND NEITHER SHARE VIEW ──
 * The two share views do not answer "is the vehicle paid for". They answer what a partner is paid
 * and what Primelane retains, and both of those streams are computed off allocations alone — no
 * partner has ever been paid a share of a contract deposit, because the payout base reads repayments
 * and a deposit is not one. Folding the deposit into them would put money in a card labelled
 * "Payout" that nobody was paid. So the share views are left exactly as they were, which is also
 * what the ruling says about partner figures [D-B58], and the deposit counts in the All view, where
 * the question is whether the price has been met. In every view the row still reads Outstanding =
 * Total Repayable less Repaid.
 *
 * ── REPAID IS NOT CLAMPED, OUTSTANDING IS ──
 * Outstanding is a balance, and a balance cannot be negative; it has always floored at nothing and
 * still does. Repaid is a statement about money that moved, and it stays literal: on a contract
 * whose payments have run past its price it reads more than the price, which is exactly what the
 * engine has allocated and exactly the overshoot D-B58 is about. Clamping it would hide the thing
 * the admin needs to see, and would make the card disagree with the Payments tab, which counts the
 * same cedis.
 */
export function vehicleRepaid(input: VehicleRepaidInput): VehicleRepaid {
    const deposit = input.mode === 'all' ? input.netDeposit || 0 : 0;
    const price = input.price * input.modeMultiplier;
    const repaid = input.contractAllocation * input.modeMultiplier + deposit;

    return {
        price,
        repaid,
        outstanding: Math.max(0, price - repaid),
    };
}
